/**
 * Custom Drum Kit storage — user-tunable kit presets that clone the built-in
 * synth kits and override individual DSP params, persisted in localStorage.
 *
 * Schema (one entry):
 *   { id:        "custom-<random>",         // unique
 *     label:     "My Trap Kit",             // user-supplied display name
 *     basedOn:   "synth-808",               // built-in id the user cloned
 *     params:    { kick:{…}, snare:{…}, … } // full struct (KIT_PARAM_SCHEMA shape)
 *     createdAt: ISO date string }
 *
 * Storage key: `elpm-custom-drum-kits` → JSON array of entries (newest first).
 *
 * No backend round-trip — these live entirely in the browser. If the user
 * wants to share their kits across machines we'll need a backend table later.
 */

import { SYNTH_KIT_PRESETS, KIT_PARAM_SCHEMA } from "./synthDrumKits.js";

const STORAGE_KEY = "elpm-custom-drum-kits";

/** Read every saved custom kit. Returns [] on parse failure or absent key. */
export function loadCustomKits() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveCustomKits(kits) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(kits));
}

/** Deep-clone the params of a built-in kit (the user's starting point). */
export function cloneBuiltinParams(builtinId) {
  const src = SYNTH_KIT_PRESETS[builtinId];
  if (!src) return null;
  return structuredClone(src);
}

/** Save a new custom kit. Returns the persisted entry (with assigned id). */
export function addCustomKit({ label, basedOn, params }) {
  const safeLabel = (label || "").trim().slice(0, 40) || "Untitled kit";
  const safeBase = SYNTH_KIT_PRESETS[basedOn] ? basedOn : "synth-808";
  // Clamp every param to its schema range so corrupt input can't slip in.
  const clamped = {};
  for (const voice of Object.keys(KIT_PARAM_SCHEMA)) {
    clamped[voice] = {};
    const schema = KIT_PARAM_SCHEMA[voice];
    const src = (params && params[voice]) || SYNTH_KIT_PRESETS[safeBase][voice];
    for (const k of Object.keys(schema)) {
      const [min, max] = schema[k];
      const v = typeof src[k] === "number" ? src[k] : (min + max) / 2;
      clamped[voice][k] = Math.max(min, Math.min(max, v));
    }
  }
  const entry = {
    id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    label: safeLabel,
    basedOn: safeBase,
    params: clamped,
    createdAt: new Date().toISOString(),
  };
  const all = loadCustomKits();
  all.unshift(entry);
  saveCustomKits(all);
  return entry;
}

/** Update an existing custom kit's params (after the user re-tunes its sliders). */
export function updateCustomKit(id, patch) {
  const all = loadCustomKits();
  const idx = all.findIndex((k) => k.id === id);
  if (idx < 0) return null;
  const next = { ...all[idx], ...patch };
  // Re-clamp if params are being patched.
  if (patch.params) {
    const clamped = {};
    for (const voice of Object.keys(KIT_PARAM_SCHEMA)) {
      clamped[voice] = {};
      const schema = KIT_PARAM_SCHEMA[voice];
      const src = patch.params[voice] || all[idx].params[voice];
      for (const k of Object.keys(schema)) {
        const [min, max] = schema[k];
        const v = typeof src[k] === "number" ? src[k] : (min + max) / 2;
        clamped[voice][k] = Math.max(min, Math.min(max, v));
      }
    }
    next.params = clamped;
  }
  all[idx] = next;
  saveCustomKits(all);
  return next;
}

/** Delete a custom kit by id. Returns true on success. */
export function removeCustomKit(id) {
  const all = loadCustomKits();
  const next = all.filter((k) => k.id !== id);
  if (next.length === all.length) return false;
  saveCustomKits(next);
  return true;
}

/** Look up a custom kit by id. */
export function findCustomKit(id) {
  return loadCustomKits().find((k) => k.id === id) || null;
}

export function isCustomKitId(id) {
  return typeof id === "string" && id.startsWith("custom-");
}
